var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AsciiText } from './framework/AsciiText';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
export class SonyBraviaRs232 {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.framework = framework;
        // other class properties and constructor
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x83, 0x00, 0x00, 0xff, 0xff]), /\x70([\s\S]{3})[\s\S]/, tags);
                // response is 0x70 = answer, 0x00 = completed, 0x02 = data size, power status, checksum
                if (match[1].charCodeAt(0) === 0)
                    return match[1].charCodeAt(2) !== 0;
                return this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                // Bravia does not accept commands after the power has been turned on for approximately
                // 20 seconds.
                this.framework.suspendPolling(power ? 20000 : 1000);
                const match = yield this.socket.send(this.makeCommand([0x8c, 0x00, 0x00, 0x02, power ? 0x01 : 0x00]), /\x70([\s\S])[\s\S]/, tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), power ? 20000 : 2000);
                return match[1].charCodeAt(0) === 0 ? power : this.state.power.value;
            }),
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x83, 0x00, 0x02, 0xff, 0xff]), /\x70([\s\S]{4})[\s\S]/, tags);
                if (match[1].charCodeAt(0) === 0 && match[1].charCodeAt(1) === 3) {
                    const codeHi = (match[1].charCodeAt(2) << 8) & 0xff00;
                    const codeLo = match[1].charCodeAt(3);
                    return (codeHi + codeLo).toString(16);
                }
                return this.state.videoInput.value;
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                const codeInt = parseInt(code, 16);
                const codeHi = (codeInt >> 8) & 0x00ff;
                const codeLo = codeInt & 0x00ff;
                const match = yield this.socket.send(this.makeCommand([0x8c, 0x00, 0x02, 0x03, codeHi, codeLo]), /\x70([\s\S])[\s\S]/, tags);
                if (match[1].charCodeAt(0) === 0)
                    return code;
                return this.state.videoInput.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.volume.value; // there is no volume query command
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x8c, 0x00, 0x05, 0x03, 0x01, volume]), /\x70([\s\S])[\s\S]/, tags);
                if (match[1].charCodeAt(0) === 0)
                    return volume;
                return this.state.volume.value;
            }),
            powerOffRestrictions: ['get', 'set'],
            range: { max: 100, min: 0, round: 1 },
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.volumeMute.value; // there is no volume mute query command
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x8c, 0x00, 0x06, 0x03, 0x01, mute ? 1 : 0]), /\x70([\s\S])[\s\S]/, tags);
                if (match[1].charCodeAt(0) === 0)
                    return mute;
                return this.state.volumeMute.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                controls: ['power', 'videoInput'],
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const initializerSteps = [
                {
                    errorCode: 'verifyFailed',
                    send: this.makeCommand([0x83, 0x00, 0x00, 0xff, 0xff]),
                    next: /\x70[\s\S]+/i,
                },
            ];
            return yield CommandSocketInitializer.initialize(this.socket, initializerSteps, credentials);
        });
    }
    // other class methods
    makeCommand(data) {
        const bytes = [...data];
        const checksum = bytes.reduce((sum, p) => sum + p);
        bytes.push(checksum & 0x00ff);
        return AsciiText.decode(bytes);
    }
}
// static properties
SonyBraviaRs232.driverType = 'socketDriver';
SonyBraviaRs232.key = '-NT9I4XhJiOMgpfMxw9V';
SonyBraviaRs232.socketOptions = [
    {
        commandExtractor: (buffer) => {
            for (let i = 0; i < buffer.length - 2; i++) {
                if (buffer.charCodeAt(i) === 0x70) {
                    let checksum = buffer.charCodeAt(i);
                    let j = 1;
                    // response is 0x70 resultCode numBytes byte1 ... byteN checksum
                    while (j < buffer.length - 1) {
                        checksum += buffer.charCodeAt(i + j);
                        checksum &= 0x00ff;
                        // Response commands can be as short as 70 00 70 which means j = 1
                        if (j >= 1 && checksum === buffer.charCodeAt(i + j + 1)) {
                            const command = buffer.slice(i, i + j + 2); // include the checksum
                            buffer = buffer.slice(i + j + 2);
                            return { command, buffer };
                        }
                        j++;
                    }
                }
            }
            return undefined;
        },
        isBinary: true,
        port: 4999,
        sendTerminator: '',
    },
];
